import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, Search, GraduationCap, Globe, Wallet, Layers,
  RotateCcw, Sparkles, Gift, Award, Mail, Loader2, CheckCircle2, X, Save,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import CourseResultCard from "../components/CourseResultCard";
import ScholarshipAlert from "../components/ScholarshipAlert";
import { COUNTRIES, FIELDS, LEVELS, BUDGETS, COURSES } from "../data/courses";
import { matchScholarships } from "../data/scholarships";
import { api } from "../lib/api";
import { useToast } from "../hooks/use-toast";

const STEPS = [
  { key: "field", title: "What do you want to study?", icon: Layers, options: FIELDS },
  { key: "level", title: "Which level are you aiming for?", icon: GraduationCap, options: LEVELS },
  { key: "country", title: "Preferred destination?", icon: Globe, options: ["Any", ...COUNTRIES] },
  { key: "budget", title: "What's your yearly budget?", icon: Wallet, options: BUDGETS.map((b) => b.label) },
];

export default function CourseFinder() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [step, setStep] = useState(0);
  const [ans, setAns] = useState({});
  const [done, setDone] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveForm, setSaveForm] = useState({ name: "", email: "" });
  const [saveState, setSaveState] = useState("idle");

  const choose = (val) => {
    const next = { ...ans, [STEPS[step].key]: val };
    setAns(next);
    if (step < STEPS.length - 1) setStep(step + 1);
    else setDone(true);
  };

  const results = useMemo(() => {
    if (!done) return [];
    const budgetMax = (BUDGETS.find((b) => b.label === ans.budget) || {}).max || 999;
    return COURSES.map((c) => {
      let score = 60;
      if (c.field === ans.field) score += 20;
      if (c.level === ans.level) score += 10;
      if (ans.country && ans.country !== "Any" && c.country === ans.country) score += 8;
      if (c.fees <= budgetMax) score += 7; else score -= 15;
      score = Math.max(42, Math.min(98, score + (c.qs <= 30 ? 3 : 0)));
      return { ...c, fit: score };
    })
      .filter((c) => c.fees <= budgetMax + 5)
      .sort((a, b) => b.fit - a.fit)
      .slice(0, 6);
  }, [done, ans]);

  const scholarships = useMemo(() => (done ? matchScholarships(ans).slice(0, 4) : []), [done, ans]);

  useEffect(() => {
    if (done && results.length) {
      localStorage.setItem("cc_finder", JSON.stringify({
        criteria: ans,
        items: results.map((c) => ({
          id: c.id, course: c.course, university: c.university, country: c.country,
          fit: c.fit, fees: c.fees, duration: c.duration,
        })),
        at: new Date().toISOString(),
      }));
    }
  }, [done, results, ans]);

  const reset = () => { setStep(0); setAns({}); setDone(false); };

  const saveMatches = async (e) => {
    e.preventDefault();
    setSaveState("sending");
    try {
      await api.post("/matches", {
        name: saveForm.name,
        email: saveForm.email,
        criteria: ans,
        items: results.map((c) => ({
          course: c.course, university: c.university, country: c.country,
          fit: c.fit, fees: c.fees, duration: c.duration,
        })),
      });
      setSaveState("sent");
      toast({ title: "Matches saved!", description: "We've emailed a copy to you." });
      setTimeout(() => { setSaveOpen(false); setSaveState("idle"); setSaveForm({ name: "", email: "" }); }, 2500);
    } catch (err) {
      setSaveState("idle");
      toast({ title: "Could not save", description: "Please try again.", variant: "destructive" });
    }
  };

  return (
    <div className="min-h-screen bg-[#f6f8fc] flex flex-col">
      <Header />
      <main className="flex-1">
        <section className="bg-[#16244F] py-14 md:py-16">
          <div className="max-w-[1240px] mx-auto px-5 text-center">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#F58220]/20 text-[#F9B57C] text-xs font-semibold font-head">
              <Sparkles size={14} /> SMART COURSE FINDER
            </span>
            <h1 className="font-head text-3xl md:text-5xl font-bold text-white mt-4">
              Find Your Best-Fit Course in <span className="text-[#F9B57C]">60 Seconds</span>
            </h1>
            <p className="text-white/75 mt-4 max-w-2xl mx-auto">
              Answer 4 quick questions and get matched to universities and programs with a personalised Fit Score.
            </p>
          </div>
        </section>

        <section className="max-w-[1100px] mx-auto px-5 py-10 md:py-14">
          {!done ? (
            <div className="max-w-[1000px] mx-auto bg-white rounded-2xl cc-shadow-card p-7 md:p-10">
              <div className="flex items-center gap-2 mb-6">
                {STEPS.map((s, i) => (
                  <div key={s.key} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-[#F58220]" : "bg-[#eef1f6]"}`} />
                ))}
              </div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 rounded-xl bg-[#eaf3ff] text-[#2b8cf0] flex items-center justify-center">
                  {React.createElement(STEPS[step].icon, { size: 22 })}
                </div>
                <div>
                  <div className="text-xs text-[#8a90a0] font-head">Step {step + 1} of {STEPS.length}</div>
                  <h2 className="font-head text-xl md:text-2xl font-semibold text-[#16244F]">{STEPS[step].title}</h2>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {STEPS[step].options.map((opt) => (
                  <button key={opt} onClick={() => choose(opt)}
                    className={`text-left px-5 py-4 rounded-xl border-2 font-medium text-[#16244F] transition-all hover:border-[#2b8cf0] hover:bg-[#eaf3ff] ${ans[STEPS[step].key] === opt ? "border-[#2b8cf0] bg-[#eaf3ff]" : "border-gray-100"}`}>
                    {opt}
                  </button>
                ))}
              </div>
              {step > 0 && (
                <button onClick={() => setStep(step - 1)} className="mt-6 inline-flex items-center gap-1.5 text-[#5a6274] hover:text-[#16244F] font-head text-sm">
                  <ArrowLeft size={16} /> Back
                </button>
              )}
            </div>
          ) : (
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <div>
                  <h2 className="font-head text-2xl font-bold text-[#16244F]">Your Top Matches</h2>
                  <p className="text-[#5a6274] text-sm mt-1">{ans.field} · {ans.level} · {ans.country} · {ans.budget}</p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  {results.length > 0 && (
                    <button onClick={() => setSaveOpen(true)} className="inline-flex items-center gap-2 bg-[#2b8cf0] hover:bg-[#1f7ad6] text-white font-head font-semibold px-5 py-2.5 rounded-lg transition-colors">
                      <Save size={16} /> Save & Email
                    </button>
                  )}
                  <button onClick={reset} className="inline-flex items-center gap-2 text-[#5a6274] hover:text-[#16244F] font-head font-semibold">
                    <RotateCcw size={16} /> Start Over
                  </button>
                </div>
              </div>

              {results.length === 0 ? (
                <div className="bg-white rounded-2xl cc-shadow-card p-12 text-center text-[#5a6274]">
                  No direct matches for this budget. <button onClick={reset} className="text-[#F58220] font-semibold">Adjust your answers</button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
                  {results.map((c) => <CourseResultCard key={c.id} c={c} />)}
                </div>
              )}

              {/* Scholarship Finder */}
              {scholarships.length > 0 && (
                <div className="mt-12">
                  <h2 className="font-head text-2xl font-bold text-[#16244F] flex items-center gap-2">
                    <Gift size={22} className="text-[#F58220]" /> Scholarships You May Qualify For
                  </h2>
                  <p className="text-[#5a6274] text-sm mt-1 mb-6">Based on your selected field, level and destination.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {scholarships.map((s) => (
                      <div key={s.id} className="bg-white rounded-2xl cc-shadow-card p-6 flex gap-4">
                        <div className="shrink-0 w-12 h-12 rounded-xl bg-[#F58220]/10 text-[#F58220] flex items-center justify-center"><Award size={24} /></div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-head font-semibold text-[#16244F]">{s.name}</h3>
                            <span className="text-xs font-bold text-[#16a34a] bg-[#16a34a]/10 px-2 py-0.5 rounded-full">{s.amount}</span>
                          </div>
                          <div className="text-xs text-[#8a90a0] mt-0.5">{s.provider}</div>
                          <p className="text-sm text-[#5a6274] mt-2">{s.note}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-8">
                <ScholarshipAlert defaults={ans} />
              </div>

              <div className="mt-12 rounded-2xl bg-[#eaf3ff] p-7 text-center">
                <h3 className="font-head text-xl font-bold text-[#16244F]">Want expert help shortlisting?</h3>
                <p className="text-[#5a6274] mt-2">Book a free session with our counsellors to finalise your application plan.</p>
                <button onClick={() => navigate("/", { state: { scrollTo: "contact" } })} className="mt-5 inline-flex items-center gap-2 bg-[#16244F] hover:bg-[#223464] text-white font-head font-semibold px-6 py-3 rounded-lg transition-colors">
                  <Search size={17} /> Talk to a Counsellor
                </button>
              </div>
            </div>
          )}
        </section>
      </main>
      <Footer />

      {/* Save matches modal */}
      {saveOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <form onSubmit={saveMatches} className="bg-white rounded-2xl w-full max-w-md p-7">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-head text-xl font-bold text-[#16244F]">Email My Matches</h3>
              <button type="button" onClick={() => setSaveOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={22} /></button>
            </div>
            <p className="text-sm text-[#5a6274] mb-5">We'll send your {results.length} top matches to your inbox.</p>
            <div className="space-y-4">
              <input required placeholder="Your name" value={saveForm.name} onChange={(e) => setSaveForm({ ...saveForm, name: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] outline-none" />
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a90a0]" />
                <input required type="email" placeholder="Your email" value={saveForm.email} onChange={(e) => setSaveForm({ ...saveForm, email: e.target.value })} className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] outline-none" />
              </div>
              <button type="submit" disabled={saveState !== "idle"} className="w-full inline-flex items-center justify-center gap-2 bg-[#F58220] hover:bg-[#e0730f] text-white font-head font-semibold py-3.5 rounded-lg transition-colors disabled:opacity-80">
                {saveState === "sending" ? (<><Loader2 size={18} className="animate-spin" /> Sending...</>)
                  : saveState === "sent" ? (<><CheckCircle2 size={18} /> Sent!</>)
                  : (<><Mail size={18} /> Email My Matches</>)}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
